import { SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { ThemeToggle } from './ThemeToggle';
import { LanguageToggle } from './LanguageToggle';
import { UserMenu } from './UserMenu';
import { AskAssistantLauncher } from '@/components/ai/AskAssistantLauncher';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useLocation, Link } from 'react-router-dom';
import { useDynamicModules } from '@/api/hooks/useWorkflows';

export function Header() {
  const location = useLocation();
  const rawSegments = location.pathname.split('/').filter(Boolean);
  // Cached by the nav merger — no extra request; [] for tenants without
  // dynamic modules.
  const { data: dynamicModules = [] } = useDynamicModules();

  // Routes that render their own in-page navigation get a truncated
  // shell breadcrumb — raw URL segments like record ids or module codes
  // read as noise ("26 > M > Pc_order_batches").
  const pathSegments =
    rawSegments[0] === 'clients-hub' || rawSegments[0] === 'dynamic'
      ? rawSegments.slice(0, 1)
      : rawSegments;

  const breadcrumbItems = pathSegments.map((segment, index) => {
    const href = '/' + pathSegments.slice(0, index + 1).join('/');
    const title = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    const isLast = index === pathSegments.length - 1;

    return { href, title, isLast };
  });

  // /dynamic/{code} — show the module's tenant-authored name (mirrors how
  // first-party pages surface their title here) instead of the segment
  // "Dynamic". Falls back to nothing while the catalog loads.
  if (rawSegments[0] === 'dynamic' && rawSegments[1]) {
    const moduleName = dynamicModules.find((m) => m.code === rawSegments[1])?.name;
    breadcrumbItems.splice(0, breadcrumbItems.length,
      { href: location.pathname, title: moduleName ?? '', isLast: true });
  }

  return (
    <header className="sticky top-0 z-50 flex h-16 shrink-0 items-center gap-2 border-b bg-background/80 px-4 backdrop-blur-md">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbItems.map((item, index) => (
            <span key={item.href} className="flex items-center gap-2">
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {item.isLast ? (
                  <BreadcrumbPage>{item.title}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.href}>{item.title}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </span>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-2">
        <AskAssistantLauncher />
        <LanguageToggle />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
